import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Button, Center, HStack, Icon, Spinner, Text, VStack, Wrap } from '@chakra-ui/react';
import { AdvisorDirectoryCard, AdvisorDirectoryCardAdvisor } from '@src/components/sco/Advisors/cards/AdvisorDirectoryCard';
import { DIR_CATEGORIES } from '@src/components/sco/Advisors/data/dirCategories';
import { AdvisorsPaths, useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { LANDING_CARD_COUNT, LANDING_SKILL_CHIPS, LandingChip, landingSeeAllHref, pickCategoryChips, pickSkillCandidates, pickSkillChips } from '@src/components/sco/Advisors/utils/landingBrowseChips';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISORS_LANDING_BROWSE, ADVISORS_LANDING_FILTERED, ADVISORS_LANDING_SKILL_COUNTS, LANDING_SKILL_SLOTS, landingCategoryAlias } from '@src/gql/queries/AdvisorsLandingBrowse';
import { useMemo, useState } from 'react';
import { LuArrowRight } from 'react-icons/lu';
type CountField = {
  pagination?: {
    itemCount?: number | null;
  } | null;
} | null;
type BrowseData = {
  cards?: {
    items?: Array<AdvisorDirectoryCardAdvisor | null> | null;
  };
  sample?: {
    items?: Array<{
      id: string;
      skillTagTitles?: string[] | null;
    } | null> | null;
  };
} & Record<string, CountField | undefined>;
type FilteredData = {
  advisorDirectory?: {
    items?: Array<AdvisorDirectoryCardAdvisor | null> | null;
  };
};
const withUser = <T extends AdvisorDirectoryCardAdvisor,>(items: Array<T | null> | null | undefined): T[] => (items ?? []).filter((item): item is T => !!item?.user?.userId);
export type AdvisorsLandingBrowseViewProps = {
  paths: Pick<AdvisorsPaths, 'directory' | 'profile' | 'withQuery'>;
  chips: LandingChip[];
  selected: LandingChip | null;
  onSelect: (chip: LandingChip | null) => void;
  advisors: AdvisorDirectoryCardAdvisor[];
  loading?: boolean;
};

/**
 * Presentational landing browse block (L10): chips, 8 cards (4 across × 2 rows on desktop, a
 * horizontal scroller on phones), "See all" carrying the filter. No prices (L1).
 */
export const AdvisorsLandingBrowseView = t0 => {
  const $ = _c(19);
  const {
    paths,
    chips,
    selected,
    onSelect,
    advisors,
    loading: t1
  } = t0;
  const loading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== paths || $[1] !== selected) {
    t2 = landingSeeAllHref(paths, selected);
    $[0] = paths;
    $[1] = selected;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const seeAllHref = t2;
  let t3;
  if ($[3] !== chips || $[4] !== onSelect || $[5] !== selected) {
    t3 = chips.length > 0 && <Wrap aria-label="Filter advisors" gap={2} justify="center" role="group"><Button aria-pressed={!selected} borderRadius="full" size="sm" variant={!selected ? "solid" : "outline"} onClick={() => onSelect(null)}>All</Button>{chips.map(chip => {
        const isActive = selected?.kind === chip.kind && selected.key === chip.key;
        return <Button key={`${chip.kind}:${chip.key}`} aria-pressed={isActive} borderRadius="full" size="sm" variant={isActive ? "solid" : "outline"} onClick={() => onSelect(isActive ? null : chip)}>{chip.label}</Button>;
      })}</Wrap>;
    $[3] = chips;
    $[4] = onSelect;
    $[5] = selected;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== advisors || $[8] !== loading || $[9] !== paths) {
    t4 = loading && advisors.length === 0 ? <Center minHeight="16rem"><Spinner /></Center> : advisors.length === 0 ? <Center minHeight="8rem"><Text color="fg.muted">No advisors to show yet.</Text></Center> : <Box data-testid="landing-advisor-cards" display={{
      base: "flex",
      md: "grid"
    }} gap={4} gridTemplateColumns={{
      md: "repeat(2, minmax(0, 1fr))",
      lg: "repeat(4, minmax(0, 1fr))"
    }} marginX={{
      base: -4,
      md: 0
    }} overflowX={{
      base: "auto",
      md: "visible"
    }} paddingBottom={{
      base: 2,
      md: 0
    }} paddingX={{
      base: 4,
      md: 0
    }} scrollSnapType={{
      base: "x mandatory",
      md: "none"
    }}>{advisors.slice(0, LANDING_CARD_COUNT).map(advisor => {
        const slug = advisor.user?.slug || advisor.user?.userId;
        return <Box key={advisor.advisorProfileId} flex={{
          base: "0 0 75%",
          sm: "0 0 45%",
          md: "initial"
        }} minWidth={0} scrollSnapAlign="start"><AdvisorDirectoryCard compact={true} advisor={advisor} profileHref={`${paths.profile}/${slug}`} /></Box>;
      })}</Box>;
    $[7] = advisors;
    $[8] = loading;
    $[9] = paths;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = selected ? `See all ${selected.label} advisors` : "See all advisors";
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon><LuArrowRight /></Icon>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== seeAllHref || $[13] !== t5) {
    t7 = <HStack justify="center"><Button asChild={true} variant="cta"><NextLink href={seeAllHref}>{t5}{t6}</NextLink></Button></HStack>;
    $[12] = seeAllHref;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t7) {
    t8 = <VStack align="stretch" gap={6} marginX="auto" maxWidth="container.xl" width="full">{t3}{t4}{t7}</VStack>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};

/**
 * Data wrapper. Server-rendered with the landing: one query for the 8 default cards, a slim
 * 60-advisor rank-ordered sample, and per-category counts, then one for skill-chip counts.
 * Directory `aggregates` are admin-only, so:
 * - category chips: real per-category COUNTs (this viewer's visibility) → top 5, hide < 3;
 * - skill chips: candidates are the most-used skill tags in the 60-advisor sample (category labels
 *   excluded), then each is counted with the directory keyword search — the same search "See all"
 *   opens — and the first 4 with ≥ 3 matches show.
 */
export const AdvisorsLandingBrowseBlock = () => {
  const $ = _c(39);
  const paths = useAdvisorsPaths();
  const [selected, setSelected] = useState(null);
  const browse = useQuery(ADVISORS_LANDING_BROWSE);
  const t0 = browse.data?.cards?.items;
  let t1;
  if ($[0] !== t0) {
    t1 = withUser(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cards = t1;
  let t2;
  if ($[2] !== browse.data?.sample?.items) {
    t2 = browse.data?.sample?.items ?? [];
    $[2] = browse.data?.sample?.items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = t2.filter(Boolean);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const sample = t3;
  let t4;
  if ($[6] !== browse.data) {
    const counts = {};
    for (const cat of DIR_CATEGORIES) {
      counts[cat.key] = Number(browse.data?.[landingCategoryAlias(cat.key)]?.pagination?.itemCount ?? 0);
    }
    t4 = pickCategoryChips(DIR_CATEGORIES, counts);
    $[6] = browse.data;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const categoryChips = t4;
  let t5;
  if ($[8] !== sample) {
    t5 = pickSkillCandidates(sample.map(_temp), DIR_CATEGORIES, LANDING_SKILL_SLOTS);
    $[8] = sample;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const skillCandidates = t5;
  let t6;
  if ($[10] !== skillCandidates) {
    t6 = Object.fromEntries(Array.from({
      length: LANDING_SKILL_SLOTS
    }, (_, i) => [`s${i}`, skillCandidates[i] ?? null]));
    $[10] = skillCandidates;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = skillCandidates.length === 0;
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = {
      variables: t6,
      skip: t7
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const skillCounts = useQuery(ADVISORS_LANDING_SKILL_COUNTS, t8);
  let t9;
  if ($[15] !== skillCandidates || $[16] !== skillCounts.data) {
    let t10;
    if ($[18] !== skillCounts.data) {
      t10 = (__0, i_0) => skillCounts.data?.[`s${i_0}`]?.pagination?.itemCount;
      $[18] = skillCounts.data;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    t9 = pickSkillChips(skillCandidates, skillCandidates.map(t10), LANDING_SKILL_CHIPS);
    $[15] = skillCandidates;
    $[16] = skillCounts.data;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const skillChips = t9;
  const t10 = selected?.kind === "category" ? selected.key : undefined;
  const t11 = selected?.kind === "skill" ? selected.key : undefined;
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = {
      categoryKey: t10,
      keywords: t11
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = !selected;
  let t14;
  if ($[23] !== t12 || $[24] !== t13) {
    t14 = {
      variables: t12,
      skip: t13
    };
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const filtered = useQuery(ADVISORS_LANDING_FILTERED, t14);
  let t15;
  if ($[26] !== cards || $[27] !== filtered || $[28] !== selected) {
    t15 = selected ? withUser(filtered.data?.advisorDirectory?.items) : cards;
    $[26] = cards;
    $[27] = filtered;
    $[28] = selected;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const advisors = t15;
  const loading = selected ? filtered.loading : browse.loading;
  if (browse.error && !browse.data) {
    return null;
  }
  let t16;
  if ($[30] !== categoryChips || $[31] !== skillChips) {
    t16 = [...categoryChips, ...skillChips];
    $[30] = categoryChips;
    $[31] = skillChips;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== advisors || $[34] !== loading || $[35] !== paths || $[36] !== selected || $[37] !== t16) {
    t17 = <AdvisorsLandingBrowseView advisors={advisors} chips={t16} loading={loading} paths={paths} selected={selected} onSelect={setSelected} />;
    $[33] = advisors;
    $[34] = loading;
    $[35] = paths;
    $[36] = selected;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
};
function _temp(item) {
  return item.skillTagTitles ?? [];
}